import React, { useState, useEffect } from "react";
import { Button, CircularProgress } from "@mui/material";
import { useLocation } from "react-router-dom";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import AbcIcon from "@mui/icons-material/Abc";
import { opMetadataFetch, optMetadataFetch } from "../actions/dcm_actions";
import { extractLocalDicomMetaTable } from "../utils/localDicomMetaExtract";
import DCMMetaTable, { isDicomMetaTableData } from "./components/MetaTable";

import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useDispatch, useSelector } from "react-redux";

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
async function resolveOpMetadata({ apiData, url }) {
  if (isDicomMetaTableData(apiData)) return apiData;

  if (url) {
    const fromApi = await opMetadataFetch(url);
    if (isDicomMetaTableData(fromApi)) return fromApi;

    const fromLocal = await extractLocalDicomMetaTable(url);
    if (isDicomMetaTableData(fromLocal)) {
      return fromLocal;
    }
  }

  return null;
}

async function resolveOptMetadata({ apiData, url, cachedDataSet }) {
  if (isDicomMetaTableData(apiData)) return apiData;

  if (url) {
    const fromApi = await optMetadataFetch(url);
    if (isDicomMetaTableData(fromApi)) return fromApi;
  }

  const fromLocal = await extractLocalDicomMetaTable(url, cachedDataSet);
  if (isDicomMetaTableData(fromLocal)) {
    return fromLocal;
  }

  return null;
}

export default function MetaDataDialog(props) {
  const dispatch = useDispatch();
  const dicom_data = useSelector((state) => state.dcm);
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const stackId = query.get("stack_id");
  const stackData = dicom_data?.dicom_data?.[stackId];
  const [open, setOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState("op");
  const [metaData, setMetaData] = useState({
    op: null,
    opt: null,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const loadMetadata = async () => {
      setLoading(true);
      try {
        const [opData, optData] = await Promise.all([
          resolveOpMetadata({
            apiData: dicom_data?.opMetaData,
            url: props.urls?.op,
          }),
          resolveOptMetadata({
            apiData: dicom_data?.optMetaData,
            url: props.urls?.opt,
            cachedDataSet: stackData?.OPT?.metadata?.dataset,
          }),
        ]);

        if (cancelled) return;

        if (isDicomMetaTableData(opData)) {
          dispatch({ type: "opMetaData_changed", opMetaData: opData });
        }
        if (isDicomMetaTableData(optData)) {
          dispatch({ type: "optMetaData_changed", optMetaData: optData });
        }

        setMetaData({ op: opData, opt: optData });
      } catch (error) {
        console.error("Failed to load DICOM metadata:", error);
        if (!cancelled) {
          setMetaData({ op: null, opt: null });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadMetadata();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    props.urls?.op,
    props.urls?.opt,
    stackData?.OPT?.metadata?.dataset,
    dicom_data?.opMetaData,
    dicom_data?.optMetaData,
    dispatch,
  ]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (event, newValue) => {
    setCurrentTab(newValue === 0 ? "op" : "opt");
  };

  return (
    <>
      <span style={{ padding: 4 }}>
        <Button
          size="small"
          style={{ paddingLeft: 1, paddingRight: 1, width: 30 }}
          variant="contained"
          onClick={() => setOpen(true)}
        >
          <AbcIcon size={2} />
        </Button>
      </span>
      <Dialog open={open} onClose={handleClose} maxWidth="lg">
        <DialogTitle>Dicom Metadata</DialogTitle>
        <DialogContent style={{ minWidth: 800 }}>
          <Tabs
            value={currentTab === "op" ? 0 : 1}
            onChange={handleChange}
            aria-label="basic tabs example"
          >
            <Tab label="IR" {...a11yProps("op")} />
            <Tab label="OCT" {...a11yProps("opt")} />
          </Tabs>
          {loading ? (
            <div
              style={{ display: "flex", justifyContent: "center", padding: 24 }}
            >
              <CircularProgress size={28} />
            </div>
          ) : (
            <DCMMetaTable
              data={currentTab === "op" ? metaData.op : metaData.opt}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
